/**
 * FormSimulationIntegration - Composant d'intégration complet pour FormPollCreator
 *
 * Utilisation :
 * <FormSimulationIntegration
 *   pollTitle={draft.title}
 *   questions={draft.questions}
 *   userTier="pro"
 * />
 */

import { Suspense } from "react";
import { useTranslation } from "react-i18next";
import { useFormSimulation } from "../../hooks/useFormSimulation";
import { SimulationButton } from "./SimulationButton";
import { SimulationModal } from "./SimulationModal";
import { SimulationProgress } from "./SimulationProgress";
import { SimulationReport } from "./SimulationReport";
import { ErrorBoundary } from "@core/components/ErrorBoundary";
import type { UserTier } from "../../types/simulation";

interface FormSimulationIntegrationProps {
  /** ID du poll */
  pollId: string;

  /** Titre du poll */
  pollTitle: string;

  /** Questions du poll */
  questions: Array<{
    id: string;
    title: string;
    type: "single" | "multiple" | "text" | "long-text" | "matrix";
    required?: boolean;
    options?: Array<{ id: string; label: string }>;
    matrixRows?: Array<{ id: string; label: string }>;
    matrixColumns?: Array<{ id: string; label: string }>;
    matrixType?: "single" | "multiple";
  }>;

  /** Tier utilisateur */
  userTier: UserTier;

  /** Variant du bouton */
  buttonVariant?: "primary" | "secondary";

  /** Appliquer les suggestions IA aux questions (révision via l'agent) */
  onApplyRevision?: (suggestions: string[]) => Promise<void> | void;

  /** Indique qu'une révision IA est en cours */
  isApplyingRevision?: boolean;

  /** Déclenché quand un utilisateur free clique « Tester » (paywall SOLO) */
  onUpgrade?: () => void;
}

export function FormSimulationIntegration({
  pollId,
  pollTitle,
  questions,
  userTier,
  buttonVariant = "secondary",
  onApplyRevision,
  isApplyingRevision,
  onUpgrade,
}: FormSimulationIntegrationProps) {
  const { t } = useTranslation("forms");
  const simulation = useFormSimulation({
    pollTitle,
    questions,
    userTier,
  });

  // Ne rien afficher si pas de questions
  if (questions.length === 0) {
    return null;
  }

  // free : « Tester » est payant → le bouton ouvre le paywall au lieu du modal.
  const isPaywall = userTier === "free";

  return (
    <>
      {/* Bouton */}
      <SimulationButton
        onClick={isPaywall ? () => onUpgrade?.() : simulation.openSimulation}
        remainingSimulations={simulation.remainingToday}
        disabled={!simulation.canLaunchSimulation}
        variant={buttonVariant}
        isLocked={isPaywall}
      />

      {/* Modal configuration — boundaries locales pour éviter qu'une
          suspension ou exception dans la simulation casse toute l'UI.
          Cf. Docs/ARCHITECTURE/ADR-SUSPENSE-BOUNDARIES.md §Règle 2 */}
      {simulation.showModal && (
        <ErrorBoundary
          fallback={
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white dark:bg-gray-800 border border-red-500 rounded-lg p-6 max-w-md w-full text-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {t("simulation.error.title")}
                </h2>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  {t("simulation.error.body")}
                </p>
                <button
                  onClick={simulation.closeModal}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {t("common.close")}
                </button>
              </div>
            </div>
          }
        >
          <Suspense
            fallback={
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
              </div>
            }
          >
            <SimulationModal
              pollId={pollId}
              detectedContext={simulation.detectedContext}
              userTier={userTier}
              onStart={simulation.startSimulation}
              onClose={simulation.closeModal}
              remainingSimulations={simulation.remainingSimulations}
              remainingToday={simulation.remainingToday}
            />
          </Suspense>
        </ErrorBoundary>
      )}

      {/* Progress */}
      {simulation.isRunning && simulation.configuredVolume > 0 && (
        <SimulationProgress
          total={simulation.configuredVolume}
          current={Math.round(
            (simulation.progress / 100) * simulation.configuredVolume,
          )}
          elapsedTime={simulation.elapsedTime}
        />
      )}

      {/* Rapport */}
      {simulation.result && !simulation.isRunning && (
        <SimulationReport
          result={simulation.result}
          questions={questions}
          onClose={simulation.reset}
          isPro={userTier === "pro" || userTier === "enterprise"}
          pollId={pollId}
          onApplyRevision={onApplyRevision}
          isApplyingRevision={isApplyingRevision}
          fromCache={simulation.fromCache}
          cachedAt={simulation.cachedAt}
          onRerun={simulation.rerunSimulation}
        />
      )}
    </>
  );
}
